import * as ImagePicker from 'expo-image-picker';
import {
  Calendar,
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  DollarSign,
  Plus,
} from 'lucide-react-native';
import { ReactNode, useState } from 'react';
import {
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { DatePickerSheet } from '@/components/DatePickerSheet';
import { PhotoLightbox } from '@/components/PhotoLightbox';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { GoalPriority, GoalRow, GoalStatus } from '@/db/types';
import { parseLocalDate, toLocalYMD } from '@/lib/dates';
import {
  GOAL_CATEGORIES,
  GOAL_PRIORITY_OPTIONS,
  GOAL_STATUS_OPTIONS,
  parseExtraPhotos,
  type NewGoalInput,
} from '@/lib/goals';
import { persistPickedFile } from '@/lib/files';
import { webNoOutline } from '@/lib/web-style';

export interface GoalFormValues {
  title: string;
  why: string | null;
  category: string | null;
  target_date: number | null;
  est_cost_cents: number | null;
  status: GoalStatus;
  priority: GoalPriority;
  notes: string | null;
}

/**
 * Shared form used by both `goals/new` and `goals/[id]`. Mode is the only
 * difference between create and edit — the field set is identical.
 */
export function GoalForm({
  mode,
  initial,
  saving,
  onCancel,
  onSubmit,
  extraFooter,
}: {
  mode: 'new' | 'edit';
  initial?: GoalRow;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (input: Omit<NewGoalInput, 'propertyId'>) => Promise<void>;
  extraFooter?: ReactNode;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [why, setWhy] = useState(initial?.why ?? '');
  const [category, setCategory] = useState<string | null>(initial?.category ?? null);
  const [targetDate, setTargetDate] = useState<string>(
    initial?.target_date
      ? toLocalYMD(initial.target_date)
      : ''
  );
  const [cost, setCost] = useState<string>(
    initial?.est_cost_cents ? (initial.est_cost_cents / 100).toFixed(0) : ''
  );
  const [status, setStatus] = useState<GoalStatus>(initial?.status ?? 'someday');
  const [priority, setPriority] = useState<GoalPriority>(
    (initial?.priority as GoalPriority) ?? 2
  );
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [photo, setPhoto] = useState<string | null>(initial?.photo_uri ?? null);
  const [extraPhotos, setExtraPhotos] = useState<string[]>(
    parseExtraPhotos(initial?.extra_photos_json)
  );
  const [catOpen, setCatOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [prioOpen, setPrioOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const allPhotos = photo ? [photo, ...extraPhotos] : extraPhotos;

  const canSave = title.trim().length > 0;

  const pickPrimary = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    const persisted = await persistPickedFile(
      a.uri,
      a.fileName ?? `goal-${Date.now()}.jpg`
    );
    setPhoto(persisted);
  };

  const addExtra = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
      allowsMultipleSelection: true,
    });
    if (res.canceled || !res.assets.length) return;
    const next: string[] = [...extraPhotos];
    for (const a of res.assets) {
      const persisted = await persistPickedFile(
        a.uri,
        a.fileName ?? `goal-${Date.now()}.jpg`
      );
      next.push(persisted);
    }
    setExtraPhotos(next);
  };

  const removeExtra = (i: number) =>
    setExtraPhotos((arr) => arr.filter((_, idx) => idx !== i));

  const promoteExtra = (i: number) => {
    const newPrimary = extraPhotos[i];
    const remaining = extraPhotos.filter((_, idx) => idx !== i);
    if (photo) remaining.unshift(photo);
    setPhoto(newPrimary);
    setExtraPhotos(remaining);
  };

  const submit = async () => {
    if (!canSave) return;
    const cents = cost ? Math.round(parseFloat(cost.replace(/[^0-9.]/g, '')) * 100) : null;
    const dateMs = targetDate ? parseLocalDate(targetDate) : null;
    await onSubmit({
      title: title.trim(),
      why: why.trim() || null,
      category,
      target_date: dateMs && !isNaN(dateMs) ? dateMs : null,
      est_cost_cents: cents && !isNaN(cents) ? cents : null,
      status,
      priority,
      notes: notes.trim() || null,
      photo_uri: photo,
      extra_photos: extraPhotos,
    });
  };

  const statusLabel = GOAL_STATUS_OPTIONS.find((o) => o.v === status)?.label ?? 'Someday';
  const prioLabel = GOAL_PRIORITY_OPTIONS.find((o) => o.v === priority)?.label ?? 'Medium';

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={onCancel}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>{mode === 'new' ? 'New goal or project' : 'Edit goal or project'}</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 150 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>
          {mode === 'new' ? 'Dream it. Plan it. Build it.' : 'Refine the plan.'}
        </Text>
        <Text style={styles.sub}>
          Capture it loosely — a dream now, a real project later. Sharpen the
          date and cost as the picture clears. Status moves it from someday →
          planning → in progress → done.
        </Text>

        <View style={styles.card}>
          <Field
            label="What's the goal or project?"
            value={title}
            onChangeText={setTitle}
            placeholder="e.g. Rebuild the deck"
          />
          <Field
            label="Why does it matter?"
            value={why}
            onChangeText={setWhy}
            placeholder="The reason it's worth the money / time"
            multiline
          />
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>WHEN & WHAT</Text>
          <Pick label="Category" value={category} placeholder="Tap to pick" onPress={() => setCatOpen(true)} />
          <View style={{ marginTop: Spacing.three }}>
            <Text style={styles.fieldLabel}>TARGET DATE</Text>
            <View style={styles.dateRow}>
              <CalendarDays size={16} color={Palette.inkSoft} strokeWidth={1.8} />
              <TextInput
                value={targetDate}
                onChangeText={setTargetDate}
                placeholder="2026-12-01"
                placeholderTextColor={Palette.inkFaint}
                autoCapitalize="none"
                style={[styles.input, webNoOutline]}
              />
              <Pressable
                onPress={() => setDateOpen(true)}
                hitSlop={6}
                style={({ pressed }) => [styles.dateCalBtn, pressed && { opacity: 0.85 }]}
                accessibilityLabel="Open calendar">
                <Calendar size={16} color={Palette.forest} strokeWidth={1.8} />
              </Pressable>
            </View>
            <View style={styles.chipRow}>
              {[
                { label: 'This year', months: 6 },
                { label: 'Next year', months: 18 },
                { label: '3 years', months: 36 },
                { label: '5 years', months: 60 },
              ].map((p) => (
                <Pressable
                  key={p.label}
                  onPress={() => {
                    const d = new Date();
                    d.setMonth(d.getMonth() + p.months);
                    setTargetDate(toLocalYMD(d.getTime()));
                  }}
                  style={({ pressed }) => [styles.chip, pressed && { opacity: 0.85 }]}>
                  <Text style={styles.chipText}>{p.label}</Text>
                </Pressable>
              ))}
            </View>
          </View>
          <View style={{ marginTop: Spacing.three }}>
            <Text style={styles.fieldLabel}>ESTIMATED COST</Text>
            <View style={styles.dateRow}>
              <DollarSign size={16} color={Palette.inkSoft} strokeWidth={1.8} />
              <TextInput
                value={cost}
                onChangeText={(v) => setCost(v.replace(/[^0-9.]/g, ''))}
                placeholder="12000"
                placeholderTextColor={Palette.inkFaint}
                inputMode="decimal"
                style={[styles.input, webNoOutline]}
              />
            </View>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>STATUS & PRIORITY</Text>
          <Pick label="Status" value={statusLabel} onPress={() => setStatusOpen(true)} />
          <Pick label="Priority" value={prioLabel} onPress={() => setPrioOpen(true)} />
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>VISION BOARD</Text>
          <Pressable
            onPress={photo ? () => setLightboxIndex(0) : pickPrimary}
            onLongPress={photo ? pickPrimary : undefined}
            delayLongPress={400}
            style={styles.photoBtn}>
            {photo ? (
              <Image source={{ uri: photo }} style={styles.photo} />
            ) : (
              <View style={styles.photoEmpty}>
                <Camera size={28} color={Palette.inkFaint} strokeWidth={1.6} />
                <Text style={styles.photoLabel}>Add inspiration photo</Text>
                <Text style={styles.photoSub}>A reference, a sketch, a screenshot of what you want.</Text>
              </View>
            )}
          </Pressable>
          {photo ? (
            <Text style={styles.photoHint}>Tap to view full-size · long-press to replace.</Text>
          ) : null}

          <Text style={[styles.fieldLabel, { marginTop: Spacing.three }]}>GALLERY</Text>
          <View style={styles.galleryRow}>
            {extraPhotos.map((uri, i) => (
              <View key={`${uri}-${i}`} style={styles.galleryThumbWrap}>
                <Pressable
                  onPress={() => setLightboxIndex(photo ? i + 1 : i)}
                  onLongPress={() => promoteExtra(i)}
                  delayLongPress={350}
                  style={styles.galleryThumbPress}>
                  <Image source={{ uri }} style={styles.galleryThumb} />
                </Pressable>
                <Pressable onPress={() => removeExtra(i)} style={styles.galleryRemove} hitSlop={6}>
                  <Text style={styles.galleryRemoveText}>×</Text>
                </Pressable>
              </View>
            ))}
            <Pressable onPress={addExtra} style={styles.galleryAdd}>
              <Plus size={18} color={Palette.forest} strokeWidth={2} />
              <Text style={styles.galleryAddText}>Add</Text>
            </Pressable>
          </View>
          {extraPhotos.length > 0 ? (
            <Text style={styles.galleryHint}>Tap a thumb to make it the primary photo.</Text>
          ) : null}
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>NOTES</Text>
          <TextInput
            value={notes}
            onChangeText={setNotes}
            placeholder="Contractor names, links, ideas, anything"
            placeholderTextColor={Palette.inkFaint}
            multiline
            style={[styles.fieldInput, webNoOutline, { height: 96, textAlignVertical: 'top' }]}
          />
        </View>

        {extraFooter}
      </ScrollView>

      <StickyBottomBar>
        <Pressable
          onPress={submit}
          disabled={!canSave || saving}
          style={({ pressed }) => [
            styles.saveBtn,
            { flex: 1 },
            (!canSave || saving) && { opacity: 0.5 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>
            {saving ? 'Saving…' : mode === 'new' ? 'Add it' : 'Save changes'}
          </Text>
        </Pressable>
      </StickyBottomBar>

      <Modal animationType="fade" transparent visible={catOpen} onRequestClose={() => setCatOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setCatOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Category</Text>
            <ScrollView style={{ maxHeight: 380 }}>
              {GOAL_CATEGORIES.map((c) => (
                <Pressable
                  key={c}
                  onPress={() => { setCategory(c); setCatOpen(false); }}
                  style={({ pressed }) => [
                    styles.modalRow,
                    (pressed || category === c) && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.modalRowText}>{c}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal animationType="fade" transparent visible={statusOpen} onRequestClose={() => setStatusOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setStatusOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Status</Text>
            {GOAL_STATUS_OPTIONS.map((o) => (
              <Pressable
                key={o.v}
                onPress={() => { setStatus(o.v); setStatusOpen(false); }}
                style={({ pressed }) => [
                  styles.modalRow,
                  (pressed || status === o.v) && { backgroundColor: '#EEF3EE' },
                ]}>
                <Text style={styles.modalRowText}>{o.label}</Text>
                <Text style={styles.modalRowSub}>{o.sub}</Text>
              </Pressable>
            ))}
          </Pressable>
        </Pressable>
      </Modal>

      <Modal animationType="fade" transparent visible={prioOpen} onRequestClose={() => setPrioOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setPrioOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Priority</Text>
            {GOAL_PRIORITY_OPTIONS.map((o) => (
              <Pressable
                key={o.v}
                onPress={() => { setPriority(o.v); setPrioOpen(false); }}
                style={({ pressed }) => [
                  styles.modalRow,
                  (pressed || priority === o.v) && { backgroundColor: '#EEF3EE' },
                ]}>
                <Text style={styles.modalRowText}>{o.label}</Text>
              </Pressable>
            ))}
          </Pressable>
        </Pressable>
      </Modal>

      <DatePickerSheet
        open={dateOpen}
        value={(() => { const t = parseLocalDate(targetDate); return isNaN(t) ? null : t; })()}
        onPick={(ms) => {
          setTargetDate(toLocalYMD(ms));
          setDateOpen(false);
        }}
        onClose={() => setDateOpen(false)}
        title="Target date"
      />

      <PhotoLightbox
        uris={lightboxIndex != null ? allPhotos : null}
        startIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
      />
    </SafeAreaView>
  );
}

function Field({
  label, value, onChangeText, placeholder, multiline,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        multiline={multiline}
        style={[styles.fieldInput, webNoOutline, multiline && { height: 80, textAlignVertical: 'top' }]}
      />
    </View>
  );
}

function Pick({
  label, value, placeholder, onPress,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
  onPress: () => void;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
        <Text style={[styles.pickText, !value && styles.pickPlaceholder]}>
          {value ?? placeholder ?? 'Tap to pick'}
        </Text>
        <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  card: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  cardTitle: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  fieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  dateRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginTop: 5,
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12,
  },
  dateCalBtn: {
    width: 32, height: 32, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
  },
  input: {
    flex: 1, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  chipRow: { flexDirection: 'row', gap: 6, marginTop: 8, flexWrap: 'wrap' },
  chip: {
    paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line, backgroundColor: Palette.card,
  },
  chipText: { fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.ink },
  pickBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  pickPlaceholder: { color: Palette.inkFaint, fontFamily: Fonts.sansMedium },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink, marginBottom: 10 },
  modalRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  modalRowSub: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, marginTop: 2 },
  photoBtn: {
    marginTop: 8,
    aspectRatio: 4 / 3,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    overflow: 'hidden',
  },
  photo: { width: '100%', height: '100%' },
  photoEmpty: {
    flex: 1, alignItems: 'center', justifyContent: 'center',
    gap: 6, padding: 18,
  },
  photoLabel: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.inkSoft },
  photoSub: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkFaint,
    textAlign: 'center',
  },
  photoHint: {
    marginTop: 6, fontFamily: Fonts.sansMedium, fontSize: 12.5,
    color: Palette.inkSoft,
  },
  galleryRow: {
    marginTop: 6,
    flexDirection: 'row', flexWrap: 'wrap', gap: 8,
  },
  galleryThumbWrap: { position: 'relative' },
  galleryThumbPress: {
    width: 70, height: 70, borderRadius: 12, overflow: 'hidden',
    backgroundColor: Palette.sand,
    borderWidth: 1, borderColor: Palette.line,
  },
  galleryThumb: { width: '100%', height: '100%' },
  galleryRemove: {
    position: 'absolute', top: -6, right: -6,
    width: 20, height: 20, borderRadius: 10,
    backgroundColor: Palette.clay,
    alignItems: 'center', justifyContent: 'center',
  },
  galleryRemoveText: {
    color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13, lineHeight: 16,
  },
  galleryAdd: {
    width: 70, height: 70, borderRadius: 12,
    borderWidth: 1.5, borderStyle: 'dashed', borderColor: Palette.line,
    backgroundColor: Palette.sand,
    alignItems: 'center', justifyContent: 'center',
    gap: 2,
  },
  galleryAddText: {
    fontFamily: Fonts.sansBold, fontSize: 11.5, color: Palette.forest, letterSpacing: 0.4,
  },
  galleryHint: {
    marginTop: 8, fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
});
